import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

export default function VouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingVoucherId, setOpeningVoucherId] = useState('');

  const [form, setForm] = useState({
    voucher_type: 'RECEIPT',
    entity_type: 'CUSTOMER',
    entity_id: '',
    amount: '',
    date: '',
    mode: 'CASH',
    notes: ''
  });

  const entityOptions = useMemo(() => {
    return form.entity_type === 'CUSTOMER' ? normalizeList(customers) : normalizeList(vendors);
  }, [form.entity_type, customers, vendors]);

  const entityMap = useMemo(() => {
    const map = {};
    normalizeList(customers).forEach((c) => {
      map[c.id] = c.shop_name || c.name;
    });
    normalizeList(vendors).forEach((v) => {
      map[v.id] = v.name;
    });
    return map;
  }, [customers, vendors]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [voucherRes, customerRes, vendorRes] = await Promise.all([
        adminApi.getVouchers(),
        adminApi.getCustomers(),
        adminApi.getVendors()
      ]);
      setVouchers(normalizeList(voucherRes.data));
      setCustomers(normalizeList(customerRes.data));
      setVendors(normalizeList(vendorRes.data));
    } catch (error) {
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async () => {
    if (!form.entity_id || !form.amount) {
      toast.error('Select entity and amount');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createVoucher({
        ...form,
        amount: parseFloat(form.amount),
        date: form.date || undefined
      });
      toast.success('Voucher created');
      setForm({
        voucher_type: 'RECEIPT',
        entity_type: 'CUSTOMER',
        entity_id: '',
        amount: '',
        date: '',
        mode: 'CASH',
        notes: ''
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create voucher');
    } finally {
      setSaving(false);
    }
  };

  const handlePost = async (id) => {
    try {
      await adminApi.postVoucher(id);
      toast.success('Voucher posted');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post voucher');
    }
  };

  const handleViewPdf = async (id) => {
    setOpeningVoucherId(id);
    try {
      const response = await adminApi.getVoucherPdfUrl(id);
      const url = response?.data?.voucher_pdf_url;
      if (!url) {
        throw new Error('Voucher PDF URL unavailable');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to open voucher PDF');
    } finally {
      setOpeningVoucherId('');
    }
  };

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Vouchers</h1>
        <p className="text-[#A8A8A8] mt-1">Create receipt and payment vouchers</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Voucher Type</Label>
              <Select
                value={form.voucher_type}
                onValueChange={(value) => setForm({ ...form, voucher_type: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RECEIPT">Receipt Voucher</SelectItem>
                  <SelectItem value="PAYMENT">Payment Voucher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Entity Type</Label>
              <Select
                value={form.entity_type}
                onValueChange={(value) => setForm({ ...form, entity_type: value, entity_id: '' })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CUSTOMER">Customer</SelectItem>
                  <SelectItem value="VENDOR">Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Entity</Label>
              <Select
                value={form.entity_id}
                onValueChange={(value) => setForm({ ...form, entity_id: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {entityOptions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.shop_name || item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Amount</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Mode</Label>
              <Select
                value={form.mode}
                onValueChange={(value) => setForm({ ...form, mode: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="BANK">Bank</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="CARD">Card</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label className="text-[#5C4033]">Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {saving ? 'Saving...' : 'Create Voucher'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : vouchers.length === 0 ? (
            <div className="p-6 text-sm text-[#A8A8A8]">No vouchers yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Voucher No</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Entity</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Mode</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {vouchers.map((v) => (
                    <tr key={v.id}>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{v.voucher_no}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{v.voucher_type}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{entityMap[v.entity_id] || '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{formatCurrency(v.amount)}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{v.date ? new Date(v.date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{v.mode}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{v.status}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          {v.status === 'DRAFT' && (
                            <Button
                              variant="outline"
                              onClick={() => handlePost(v.id)}
                              className="border-[#E5E5E5]"
                            >
                              Post
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            onClick={() => handleViewPdf(v.id)}
                            disabled={openingVoucherId === v.id}
                            className="border-[#E5E5E5]"
                          >
                            {openingVoucherId === v.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                            <span className="ml-1">View PDF</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
